import { useState } from "react";
import { matchScoreClasses } from "@/lib/matchScore";
import { Bed, Bath, MapPin } from "lucide-react";
import { transformImage } from "@/lib/imageUrl";
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import type { ConciergeCard } from "@/hooks/useAiConcierge";
import { useTenant } from "@/hooks/useTenant";
import { formatRent, tenantCurrency, type Currency } from "@/lib/money";

const formatPrice = (c: ConciergeCard, currency: Currency) => formatRent(c.price, c.priceMax, currency);

const matchClass = (m: number) => matchScoreClasses(m).badge;

interface Props {
  card: ConciergeCard;
  /** Tapping the info region opens the in-place detail (no navigation). */
  onOpenDetail: (slug: string) => void;
  className?: string;
}

/**
 * A single concierge listing card. The photo strip is a finger-swipeable
 * carousel (reusing the PropertyCard pattern); the info region below is the
 * click target so a swipe never mis-fires as a tap. Used in the in-thread
 * rail and the desktop listings panel.
 */
const ConciergeListingCard = ({ card, onOpenDetail, className }: Props) => {
  const tenant = useTenant();
  const [current, setCurrent] = useState(0);
  const images = card.images?.length ? card.images : card.image ? [card.image] : [];
  const hasMultiple = images.length > 1;

  return (
    <div className={`group rounded-xl border bg-card overflow-hidden ${className ?? ""}`}>
      <div className="aspect-[3/2] overflow-hidden bg-muted relative">
        {hasMultiple ? (
          <Carousel
            className="w-full h-full [&>div]:h-full"
            opts={{ loop: true }}
            setApi={(api) => {
              if (!api) return;
              setCurrent(api.selectedScrollSnap());
              api.on("select", () => setCurrent(api.selectedScrollSnap()));
            }}
          >
            <CarouselContent className="h-full ml-0">
              {images.map((src, idx) => (
                <CarouselItem key={idx} className="h-full pl-0 touch-pan-y">
                  <img
                    src={transformImage(src, { width: 480, height: 320, quality: 70 })}
                    alt={`${card.title} (${idx + 1})`}
                    loading="lazy"
                    draggable={false}
                    className="w-full h-full object-cover select-none"
                  />
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        ) : images[0] ? (
          <img
            src={transformImage(images[0], { width: 480, height: 320, quality: 70 })}
            alt={card.title}
            loading="lazy"
            className="w-full h-full object-cover"
          />
        ) : null}
        {card.match > 0 && (
          <span
            className={`absolute top-2 left-2 z-10 px-2 py-0.5 rounded-full text-[10px] font-semibold backdrop-blur-sm ${matchClass(
              card.match,
            )}`}
          >
            {card.match}% match
          </span>
        )}
        {hasMultiple && (
          <div className="absolute bottom-2 right-2 z-10 px-2 py-0.5 rounded-full bg-charcoal/60 text-white text-[10px] font-medium backdrop-blur-sm pointer-events-none">
            {current + 1} / {images.length}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={() => onOpenDetail(card.slug)}
        className="block w-full text-left p-2.5 transition-colors hover:bg-muted/40"
        aria-label={`View details for ${card.title}`}
      >
        <p className="text-[13px] font-medium leading-tight truncate">{card.title}</p>
        <p className="text-[12px] text-muted-foreground flex items-center gap-1 mt-0.5">
          <MapPin className="h-3 w-3 shrink-0" />
          <span className="truncate">{card.area}</span>
        </p>
        <div className="flex items-center justify-between mt-1.5">
          <span className="text-primary font-semibold text-[13px]">
            {formatPrice(card, tenantCurrency(tenant))}
            <span className="text-[11px] font-normal text-muted-foreground">/mo</span>
          </span>
          <span className="text-[11px] text-muted-foreground flex items-center gap-2">
            <span className="flex items-center gap-0.5">
              <Bed className="h-3 w-3" />
              {card.beds}
            </span>
            <span className="flex items-center gap-0.5">
              <Bath className="h-3 w-3" />
              {card.baths}
            </span>
          </span>
        </div>
      </button>
    </div>
  );
};

export default ConciergeListingCard;
